import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const FUTURES_SYMBOLS = ["ES", "NQ", "CL", "GC", "SI", "ZB", "ZN", "RTY", "YM", "6E", "6J"];
const EMOTIONS = ["Confident", "Anxious", "Fearful", "Greedy", "Neutral", "FOMO", "Disciplined", "Impatient"];

// The 5 Trading Outcomes from Dr. Chris Cole
const TRADE_OUTCOMES = [
  { id: "big_win", label: "Big Win (Following Rules)", color: "emerald", emoji: "✓✓" },
  { id: "small_win", label: "Small Win (Following Rules)", color: "green", emoji: "✓" },
  { id: "break_even", label: "Break Even", color: "slate", emoji: "—" },
  { id: "small_loss", label: "Small Loss (Following Rules)", color: "yellow", emoji: "✓" },
  { id: "big_loss", label: "Big Loss (ELIMINATE THIS)", color: "red", emoji: "✗" },
];

export default function Journal() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [activeTab, setActiveTab] = useState("trades");
  const [formData, setFormData] = useState({
    symbol: "ES",
    direction: "LONG",
    entry_price: "",
    stop_loss: "",
    take_profit: "",
    position_size: "",
    order_block_identified: false,
    liquidity_grab: false,
    fu_candle: false,
    market_imbalance: false,
    daily_bias: "",
    checklist_completed: false,
    notes: "",
    emotion_before: "Neutral",
    emotion_after: "",
    followed_rules: true,
    trade_outcome: "",
    lessons_learned: "",
  });

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      const res = await axios.get(`${API}/trades`);
      setTrades(res.data);
    } catch (err) {
      toast.error("Failed to fetch trades");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      symbol: "ES",
      direction: "LONG",
      entry_price: "",
      stop_loss: "",
      take_profit: "",
      position_size: "",
      order_block_identified: false,
      liquidity_grab: false,
      fu_candle: false,
      market_imbalance: false,
      daily_bias: "",
      checklist_completed: false,
      notes: "",
      emotion_before: "Neutral",
      emotion_after: "",
      followed_rules: true,
      trade_outcome: "",
      lessons_learned: "",
    });
    setEditingTrade(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        entry_price: parseFloat(formData.entry_price),
        stop_loss: parseFloat(formData.stop_loss),
        take_profit: parseFloat(formData.take_profit),
        position_size: parseFloat(formData.position_size),
      };

      if (editingTrade) {
        await axios.patch(`${API}/trades/${editingTrade.id}`, payload);
        toast.success("Trade updated");
      } else {
        await axios.post(`${API}/trades`, payload);
        toast.success("Trade logged successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save trade");
    }
  };

  const handleCloseTrade = async (trade, exitPrice) => {
    try {
      await axios.patch(`${API}/trades/${trade.id}`, { exit_price: parseFloat(exitPrice) });
      toast.success("Trade closed");
      fetchTrades();
    } catch (err) {
      toast.error("Failed to close trade");
    }
  };

  const handleDeleteTrade = async (tradeId) => {
    if (!window.confirm("Delete this trade?")) return;
    try {
      await axios.delete(`${API}/trades/${tradeId}`);
      toast.success("Trade deleted");
      fetchTrades();
    } catch (err) {
      toast.error("Failed to delete trade");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate discipline stats
  const calculateDisciplineStats = () => {
    const closedTrades = trades.filter(t => t.status === "closed");
    const rulesFollowed = closedTrades.filter(t => t.followed_rules !== false).length;
    const bigLosses = closedTrades.filter(t => t.trade_outcome === "big_loss").length;
    
    return {
      total: closedTrades.length,
      rulesFollowed,
      disciplineRate: closedTrades.length > 0 ? Math.round((rulesFollowed / closedTrades.length) * 100) : 0,
      bigLosses,
    };
  };

  const disciplineStats = calculateDisciplineStats();

  return (
    <div className="p-6 space-y-6" data-testid="journal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold text-white">THE DISCIPLINED JOURNAL</h1>
          <p className="text-sm text-slate-500 font-mono">Eliminate large losses. Judge by discipline, not P&L.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="new-trade-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              LOG TRADE
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-mono text-white">
                {editingTrade ? "EDIT TRADE" : "NEW TRADE ENTRY"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">SYMBOL</label>
                  <Select value={formData.symbol} onValueChange={(v) => setFormData({ ...formData, symbol: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#262626] font-mono" data-testid="symbol-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                      {FUTURES_SYMBOLS.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">DIRECTION</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, direction: "LONG" })}
                      data-testid="direction-long"
                      className={`flex-1 py-2 font-mono text-sm border ${
                        formData.direction === "LONG"
                          ? "bg-emerald-500/20 border-emerald-500 text-emerald-500"
                          : "bg-transparent border-[#262626] text-slate-400"
                      }`}
                    >
                      LONG
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, direction: "SHORT" })}
                      data-testid="direction-short"
                      className={`flex-1 py-2 font-mono text-sm border ${
                        formData.direction === "SHORT"
                          ? "bg-red-500/20 border-red-500 text-red-500"
                          : "bg-transparent border-[#262626] text-slate-400"
                      }`}
                    >
                      SHORT
                    </button>
                  </div>
                </div>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">ENTRY</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.entry_price}
                    onChange={(e) => setFormData({ ...formData, entry_price: e.target.value })}
                    data-testid="entry-price-input"
                    className="w-full input-institutional px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">STOP LOSS</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.stop_loss}
                    onChange={(e) => setFormData({ ...formData, stop_loss: e.target.value })}
                    data-testid="stop-loss-input"
                    className="w-full input-institutional px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">TAKE PROFIT</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.take_profit}
                    onChange={(e) => setFormData({ ...formData, take_profit: e.target.value })}
                    data-testid="take-profit-input"
                    className="w-full input-institutional px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">SIZE</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.position_size}
                    onChange={(e) => setFormData({ ...formData, position_size: e.target.value })}
                    data-testid="position-size-input"
                    className="w-full input-institutional px-3 py-2"
                    required
                  />
                </div>
              </div>

              {/* Institutional Criteria */}
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-3">INSTITUTIONAL CRITERIA</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "order_block_identified", label: "Order Block Identified" },
                    { key: "liquidity_grab", label: "Liquidity Grabbed" },
                    { key: "fu_candle", label: "FU Candle Confirmed" },
                    { key: "market_imbalance", label: "Market Imbalance" },
                    { key: "checklist_completed", label: "Checklist Completed" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center gap-2">
                      <Checkbox
                        id={item.key}
                        checked={formData[item.key]}
                        onCheckedChange={(checked) => setFormData({ ...formData, [item.key]: checked })}
                        className="border-[#262626] data-[state=checked]:bg-blue-500"
                      />
                      <label htmlFor={item.key} className="text-sm text-slate-300">{item.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bias and Emotions */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">DAILY BIAS</label>
                  <Select value={formData.daily_bias} onValueChange={(v) => setFormData({ ...formData, daily_bias: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                      <SelectItem value="Bullish" className="font-mono">Bullish</SelectItem>
                      <SelectItem value="Bearish" className="font-mono">Bearish</SelectItem>
                      <SelectItem value="Neutral" className="font-mono">Neutral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">EMOTION BEFORE</label>
                  <Select value={formData.emotion_before} onValueChange={(v) => setFormData({ ...formData, emotion_before: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                      {EMOTIONS.map((e) => (
                        <SelectItem key={e} value={e} className="font-mono">{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">EMOTION AFTER</label>
                  <Select value={formData.emotion_after} onValueChange={(v) => setFormData({ ...formData, emotion_after: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                      <SelectValue placeholder="After close..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                      {EMOTIONS.map((e) => (
                        <SelectItem key={e} value={e} className="font-mono">{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">TRADE NOTES</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="notes-input"
                  className="w-full input-institutional px-3 py-2 h-24 resize-none"
                  placeholder="What did you observe? What was your reasoning?"
                />
              </div>

              {/* Discipline Section - Dr. Chris Cole Methodology */}
              <div className="border-t border-[#262626] pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span className="text-xs font-mono text-yellow-500">DISCIPLINED JOURNAL - DR. CHRIS COLE</span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <Checkbox
                    id="followed_rules"
                    checked={formData.followed_rules}
                    onCheckedChange={(checked) => setFormData({ ...formData, followed_rules: checked })}
                    className="border-[#262626] data-[state=checked]:bg-emerald-500"
                  />
                  <label htmlFor="followed_rules" className="text-sm text-white font-bold">
                    I FOLLOWED MY RULES ON THIS TRADE
                  </label>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-mono text-slate-500 mb-2">TRADE OUTCOME (The 5 Outcomes)</label>
                  <div className="grid grid-cols-1 gap-2">
                    {TRADE_OUTCOMES.map((outcome) => (
                      <button
                        key={outcome.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, trade_outcome: outcome.id })}
                        className={`flex items-center gap-3 p-3 border text-left transition-all ${
                          formData.trade_outcome === outcome.id
                            ? outcome.color === "red"
                              ? "border-red-500 bg-red-500/10"
                              : outcome.color === "emerald"
                              ? "border-emerald-500 bg-emerald-500/10"
                              : outcome.color === "green"
                              ? "border-green-500 bg-green-500/10"
                              : outcome.color === "yellow"
                              ? "border-yellow-500 bg-yellow-500/10"
                              : "border-slate-500 bg-slate-500/10"
                            : "border-[#262626] hover:border-slate-500"
                        }`}
                      >
                        <span className={`font-mono text-lg ${
                          outcome.color === "red" ? "text-red-500" :
                          outcome.color === "emerald" ? "text-emerald-500" :
                          outcome.color === "green" ? "text-green-500" :
                          outcome.color === "yellow" ? "text-yellow-500" :
                          "text-slate-500"
                        }`}>{outcome.emoji}</span>
                        <span className={`text-sm ${
                          formData.trade_outcome === outcome.id ? "text-white" : "text-slate-400"
                        }`}>{outcome.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">LESSONS LEARNED</label>
                  <textarea
                    value={formData.lessons_learned}
                    onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
                    className="w-full input-institutional px-3 py-2 h-20 resize-none"
                    placeholder="What did this trade teach you? How will you improve?"
                  />
                </div>
              </div>

              <button type="submit" className="w-full btn-primary py-3" data-testid="save-trade-btn">
                {editingTrade ? "UPDATE TRADE" : "LOG TRADE"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Discipline Stats Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">TOTAL TRADES</p>
          <p className="text-2xl font-mono font-bold text-white">{trades.length}</p>
        </div>
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">DISCIPLINE RATE</p>
          <p className={`text-2xl font-mono font-bold ${disciplineStats.disciplineRate >= 80 ? 'text-emerald-500' : disciplineStats.disciplineRate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
            {disciplineStats.disciplineRate}%
          </p>
        </div>
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">RULES FOLLOWED</p>
          <p className="text-2xl font-mono font-bold text-emerald-500">{disciplineStats.rulesFollowed}</p>
        </div>
        <div className="card-institutional p-4 border-red-500/30">
          <p className="text-xs font-mono text-red-500 mb-1">BIG LOSSES (ELIMINATE)</p>
          <p className="text-2xl font-mono font-bold text-red-500">{disciplineStats.bigLosses}</p>
        </div>
      </div>

      {/* Dr. Chris Cole Quote */}
      <div className="bg-yellow-500/5 border border-yellow-500/20 p-4">
        <p className="text-sm text-yellow-500/80 italic">
          "Professionals don't judge success by profits or losses, but by how well they stick to their plan. A loss with perfect execution is a WIN. A profit from breaking rules is a FAILURE."
        </p>
        <p className="text-xs text-yellow-500/60 mt-2">— Dr. Chris Cole, The Disciplined Journal</p>
      </div>

      {/* Trades Table */}
      <div className="card-institutional overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626]">
                {["DATE", "SYMBOL", "DIR", "ENTRY", "SL", "TP", "R:R", "P&L", "STATUS", "ACTIONS"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-mono text-slate-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-500 font-mono">
                    Loading trades...
                  </td>
                </tr>
              ) : trades.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-500 font-mono">
                    No trades logged yet. Start documenting your journey.
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.id} className="border-b border-[#262626] hover:bg-[#171717] transition-colors" data-testid={`trade-row-${trade.id}`}>
                    <td className="px-4 py-3 text-sm font-mono text-slate-400">{formatDate(trade.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-white font-bold">{trade.symbol}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono px-2 py-1 ${
                        trade.direction === "LONG" ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"
                      }`}>
                        {trade.direction}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-white">{trade.entry_price}</td>
                    <td className="px-4 py-3 text-sm font-mono text-red-400">{trade.stop_loss}</td>
                    <td className="px-4 py-3 text-sm font-mono text-emerald-400">{trade.take_profit}</td>
                    <td className="px-4 py-3 text-sm font-mono text-blue-400">1:{trade.risk_reward_ratio}</td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {trade.pnl !== null ? (
                        <span className={trade.pnl >= 0 ? "text-emerald-500" : "text-red-500"}>
                          ${trade.pnl.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono px-2 py-1 ${
                        trade.status === "open" ? "status-open" : 
                        trade.pnl >= 0 ? "status-win" : "status-loss"
                      }`}>
                        {trade.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {trade.status === "open" && (
                          <button
                            onClick={() => {
                              const exitPrice = prompt("Enter exit price:");
                              if (exitPrice) handleCloseTrade(trade, exitPrice);
                            }}
                            className="text-xs font-mono text-blue-500 hover:text-blue-400"
                            data-testid={`close-trade-${trade.id}`}
                          >
                            CLOSE
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTrade(trade.id)}
                          className="text-xs font-mono text-red-500 hover:text-red-400"
                          data-testid={`delete-trade-${trade.id}`}
                        >
                          DELETE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
